/**
 * CPF (Carbohydrate:Protein:Fat) Calculation Utility
 * 
 * This utility calculates nutritional values and CPF ratios based on the
 * same logic as the Flutter implementation.
 * 
 * Formula (matching Flutter exactly):
 * - Fat percentage = (fat_grams × 9 calories) / total_calories × 100
 * - Protein percentage = (protein_grams × 4 calories) / total_calories × 100
 * - Carb percentage = 100 - (fat_percentage + protein_percentage)
 */

export interface NutritionValues {
  carbs: number;
  protein: number;
  fat: number;
  calories: number;
}

export interface CPFRatio {
  c: string; // Carbohydrate percentage
  p: string; // Protein percentage
  f: string; // Fat percentage
}

export interface CPFCalculationResult {
  ratio: CPFRatio;
  isValid: boolean;
  totalCalories: number;
}

/**
 * Calculate CPF ratio from carbs, protein, fat, and calories values
 * Matches Flutter implementation exactly
 * 
 * @param carbs - Carbohydrates in grams
 * @param protein - Protein in grams
 * @param fat - Fat in grams
 * @param calories - Total calories (kcal) - this is the actual calories value, not calculated
 * @returns CPF ratio and validation status
 */
export function calculateCPFRatio(
  carbs: number,
  protein: number,
  fat: number,
  calories: number
): CPFCalculationResult {
  // Validate inputs
  if (carbs < 0 || protein < 0 || fat < 0 || calories < 0) {
    return {
      ratio: { c: "0", p: "0", f: "0" },
      isValid: false,
      totalCalories: 0,
    };
  }

  // If calories is zero, return 0:0:0
  if (calories === 0) {
    return {
      ratio: { c: "0", p: "0", f: "0" },
      isValid: true,
      totalCalories: 0,
    };
  }

  // Calculate percentages based on caloric contribution
  // This matches the Flutter logic exactly:
  // double fatPercentage = ((fatInFoodTotal.value * 9) / caloriesInFoodTotal.value * 100);
  // double proteinPercentage = ((proteinInFoodTotal.value * 4) / caloriesInFoodTotal.value * 100);
  // double carbPercentage = 100 - (fatPercentage + proteinPercentage);
  const fatPercentage = ((fat * 9) / calories) * 100;
  const proteinPercentage = ((protein * 4) / calories) * 100;
  const carbPercentage = 100 - (fatPercentage + proteinPercentage);

  return {
    ratio: {
      c: Math.round(carbPercentage).toString(),
      p: Math.round(proteinPercentage).toString(),
      f: Math.round(fatPercentage).toString(),
    },
    isValid: true,
    totalCalories: calories,
  };
}

/**
 * Calculate CPF ratio from string inputs (for form handling)
 * 
 * @param carbsStr - Carbohydrates as string
 * @param proteinStr - Protein as string
 * @param fatStr - Fat as string
 * @param caloriesStr - Total calories as string
 * @returns CPF ratio and validation status
 */
export function calculateCPFRatioFromStrings(
  carbsStr: string,
  proteinStr: string,
  fatStr: string,
  caloriesStr: string
): CPFCalculationResult {
  const carbs = parseFloat(carbsStr) || 0;
  const protein = parseFloat(proteinStr) || 0;
  const fat = parseFloat(fatStr) || 0;
  const calories = parseFloat(caloriesStr) || 0;

  return calculateCPFRatio(carbs, protein, fat, calories);
}

/**
 * Calculate total calories from macronutrients
 * 
 * @param carbs - Carbohydrates in grams
 * @param protein - Protein in grams
 * @param fat - Fat in grams
 * @returns Total calories
 */
export function calculateTotalCalories(
  carbs: number,
  protein: number,
  fat: number
): number {
  return carbs * 4 + protein * 4 + fat * 9;
}

/**
 * Format CPF ratio as display string
 * 
 * @param ratio - CPF ratio object
 * @returns Formatted string (e.g., "40 : 30 : 30")
 */
export function formatCPFRatio(ratio: CPFRatio): string {
  return `${ratio.c}:${ratio.p}:${ratio.f}`;
}

/**
 * Validate if CPF ratio percentages sum to approximately 100
 * 
 * @param ratio - CPF ratio object
 * @returns true if valid (allows for rounding differences)
 */
export function validateCPFRatio(ratio: CPFRatio): boolean {
  const sum =
    parseInt(ratio.c || "0") +
    parseInt(ratio.p || "0") +
    parseInt(ratio.f || "0");
  // Allow for small rounding differences (±2%)
  return Math.abs(sum - 100) <= 2;
}

/**
 * Parse CPF ratio string to object
 * 
 * @param ratioString - String in format "40:30:30" or "40 : 30 : 30"
 * @returns CPF ratio object
 */
export function parseCPFRatio(ratioString: string): CPFRatio | null {
  const parts = ratioString.split(":").map((s) => s.trim());
  
  if (parts.length !== 3) {
    return null;
  }

  const [c, p, f] = parts;
  
  if (!c || !p || !f) {
    return null;
  }

  return { c, p, f };
}

/**
 * Calculate macronutrients from calories and CPF ratio
 * Reverse calculation - useful for meal planning
 * 
 * @param totalCalories - Total calorie target
 * @param ratio - CPF ratio
 * @returns Macronutrient amounts in grams
 */
export function calculateMacrosFromCalories(
  totalCalories: number,
  ratio: CPFRatio
): NutritionValues {
  const carbPercent = parseInt(ratio.c || "0");
  const proteinPercent = parseInt(ratio.p || "0");
  const fatPercent = parseInt(ratio.f || "0");

  const carbCalories = (totalCalories * carbPercent) / 100;
  const proteinCalories = (totalCalories * proteinPercent) / 100;
  const fatCalories = (totalCalories * fatPercent) / 100;

  return {
    carbs: Math.round(carbCalories / 4),
    protein: Math.round(proteinCalories / 4),
    fat: Math.round(fatCalories / 9),
    calories: totalCalories,
  };
}

/**
 * Calculate average CPF ratio from min and max values
 * 
 * @param minValues - Minimum nutrition values (must include calories)
 * @param maxValues - Maximum nutrition values (must include calories)
 * @returns Average CPF ratio
 */
export function calculateAverageCPFRatio(
  minValues: { carbs: number; protein: number; fat: number; calories: number },
  maxValues: { carbs: number; protein: number; fat: number; calories: number }
): CPFRatio {
  const avgCarbs = (minValues.carbs + maxValues.carbs) / 2;
  const avgProtein = (minValues.protein + maxValues.protein) / 2;
  const avgFat = (minValues.fat + maxValues.fat) / 2;
  const avgCalories = (minValues.calories + maxValues.calories) / 2;

  const result = calculateCPFRatio(avgCarbs, avgProtein, avgFat, avgCalories);
  return result.ratio;
}

/**
 * Check if nutrition values are within acceptable range
 * 
 * @param values - Current nutrition values
 * @param minValues - Minimum acceptable values
 * @param maxValues - Maximum acceptable values
 * @returns Validation result with details
 */
export function validateNutritionRange(
  values: NutritionValues,
  minValues: Partial<NutritionValues>,
  maxValues: Partial<NutritionValues>
): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (minValues.carbs !== undefined && values.carbs < minValues.carbs) {
    errors.push(`Carbs below minimum (${minValues.carbs}g)`);
  }
  if (maxValues.carbs !== undefined && values.carbs > maxValues.carbs) {
    errors.push(`Carbs above maximum (${maxValues.carbs}g)`);
  }

  if (minValues.protein !== undefined && values.protein < minValues.protein) {
    errors.push(`Protein below minimum (${minValues.protein}g)`);
  }
  if (maxValues.protein !== undefined && values.protein > maxValues.protein) {
    errors.push(`Protein above maximum (${maxValues.protein}g)`);
  }

  if (minValues.fat !== undefined && values.fat < minValues.fat) {
    errors.push(`Fat below minimum (${minValues.fat}g)`);
  }
  if (maxValues.fat !== undefined && values.fat > maxValues.fat) {
    errors.push(`Fat above maximum (${maxValues.fat}g)`);
  }

  if (minValues.calories !== undefined && values.calories < minValues.calories) {
    errors.push(`Calories below minimum (${minValues.calories} kcal)`);
  }
  if (maxValues.calories !== undefined && values.calories > maxValues.calories) {
    errors.push(`Calories above maximum (${maxValues.calories} kcal)`);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

// Export default object with all functions for convenience
export default {
  calculateCPFRatio,
  calculateCPFRatioFromStrings,
  calculateTotalCalories,
  formatCPFRatio,
  validateCPFRatio,
  parseCPFRatio,
  calculateMacrosFromCalories,
  calculateAverageCPFRatio,
  validateNutritionRange,
};